'use client';

import {Box, Button, Checkbox, Container, styled, TextareaAutosize, TextField} from '@mui/material';
import {StyledSectionContainer} from '@/components/sections/shared/Section.styles';

export const StyledFormSectionContainer = styled(StyledSectionContainer)(({theme}) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(3, 0),
  marginBottom: theme.spacing(3),
}));

export const StyledFormSectionRichTextContainer = styled(Container)(({theme}) => ({
  width: '100%',
  alignItems: 'center',
  textAlign: 'center',
  padding: theme.spacing(2, 0, 4, 0),
}));

export const StyledFieldWrapper = styled(Box)(({theme}) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

export const StyledFieldLabel = styled('label')(({theme}) => ({
  color: theme.palette.common.white,
  fontSize: '0.9rem',
  fontFamily: theme.typography.fontFamily,
  lineHeight: 1.3,
  textAlign: 'left',
  alignSelf: 'flex-start',
}));

export const StyledFormTextField = styled(TextField)(({theme}) => ({
  width: '100%',
  backgroundColor: theme.palette.common.white,
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.form.textFieldBorder}`,

  // label — uses form.formLabel for AA-compliant contrast on the white field.
  '& .MuiInputLabel-root': {
    color: theme.palette.form.formLabel,
  },

  // target the outlined input
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadiusMd,
    backgroundColor: theme.palette.common.white,

    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: theme.shape.borderRadiusLg,
    },

    // make it thinner
    fontSize: '0.9rem',
    '& .MuiOutlinedInput-input': {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },

    // Autofill overrides (Chrome/Safari)
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & textarea:-webkit-autofill': {
      // light blue background for prefill
      WebkitBoxShadow: '0 0 0 1000px #E6F3FF inset',
      boxShadow: '0 0 0 1000px #E6F3FF inset',
      WebkitTextFillColor: theme.palette.text.dark,
      caretColor: theme.palette.text.primary,

    },

    // hover outline
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.common.white,
      borderWidth: 1,
      borderRadius: theme.shape.borderRadiusMd,
    },

    // focus outline
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.form.textFieldBorder,
      borderWidth: 1,
      borderRadius: theme.shape.borderRadiusMd,
    },

    // visible focus ring for keyboard users
    '&.Mui-focused': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
      borderRadius: theme.shape.borderRadiusMd,
    },
  },

  // text + placeholder colors for contrast against white background
  '& .MuiInputBase-input': {
    color: theme.palette.text.dark,
  },
  '& .MuiInputBase-input::placeholder': {
    color: theme.palette.form.formLabel,
    opacity: 1,
  },
}));

export const StyledGroupFieldContainer = styled(Box)(({theme}) => ({
  width: '100%',
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',
  textAlign: 'center',
  padding: 0,

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(3),
  },
}));

export const StyledGroupContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'singleGroup',
})<{ singleGroup?: boolean }>(({theme, singleGroup}) => ({
  display: 'grid',
  gridTemplateColumns: singleGroup ? '1fr auto' : 'repeat(2, 1fr)',
  gap: singleGroup ? theme.spacing(2) : theme.spacing(12),
  alignItems: singleGroup ? 'center' : 'stretch',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(6),
  },
  [theme.breakpoints.down('xs')]: {
    gap: theme.spacing(3),
  },
}));

export const StyledFormSubmitButton = styled(Button)(({theme}) => ({
  padding: theme.spacing(0.75, 6),
  letterSpacing: '0.03rem',
}));

export const StyledGroupFieldsContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  height: '100%',
  justifyContent: 'space-between',
}));

export const StyledFormSectionForm = styled('form')(({theme}) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  color: theme.palette.common.white,
  padding: theme.spacing(4, 6),
  borderRadius: theme.shape.borderRadiusLg,
  backgroundImage: "url('/images/contact-form-background.png')",
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  [theme.breakpoints.down('xs')]: {
    padding: theme.spacing(3, 3),
  },

  position: 'relative',
  overflow: 'hidden',

  // keep children above overlay
  '& > *': {
    position: 'relative',
    zIndex: 1,
  },

  // subtle overlay for contrast
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    zIndex: 0,
  },
}));

export const StyledSuccessContainer = styled('form')(({theme}) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  color: theme.palette.common.white,
  padding: theme.spacing(12, 12),
  borderRadius: theme.shape.borderRadiusLg,
  backgroundImage: "url('/images/contact-form-background.png')",
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 6),
  },

  position: 'relative',
  overflow: 'hidden',

  // keep children above overlay
  '& > *': {
    position: 'relative',
    zIndex: 1,
  },

  // subtle overlay for contrast
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    zIndex: 0,
  },
}));

export const StyledFormCheckbox = styled(Checkbox)(({theme}) => ({
  padding: 0,
  color: theme.palette.common.white,
  marginTop: theme.spacing(0.5),

  '& .MuiSvgIcon-root': {
    fontSize: '1.5rem',
  },
}));

export const StyledFormTextArea = styled(TextareaAutosize)(({theme}) => ({
  width: '100%',
  boxSizing: 'border-box',
  resize: 'none',
  minHeight: 100,

  padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
  fontSize: '0.9rem',
  fontFamily: theme.typography.fontFamily,

  backgroundColor: theme.palette.common.white,
  color: theme.palette.text.dark,

  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.form.textFieldBorder}`,

  '&::placeholder': {
    color: theme.palette.form.formLabel,
    opacity: 1,
  },

  '&:hover': {
    borderColor: theme.palette.form.textFieldBorder,
    boxShadow: `inset 0 0 0 1px ${theme.palette.form.textFieldBorder}`,
  },

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
    borderColor: theme.palette.form.textFieldBorder,
  },
}));
